<script>
import Dialog from "primevue/dialog";
import VueSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import DashboardLayout from "../layouts/DashboardLayout.vue";
import StateBlock from "../components/StateBlock.vue";
import {api} from "../services/api";

const llmProviderOptions = [
    {label: "OpenAI", value: "OpenAI"},
    {label: "Gemini 2.5 Flash", value: "Gemini25Flash"},
    {label: "Gemini 3 Flash", value: "Gemini3Flash"},
];

const embeddingProviderOptions = [
    {label: "OpenAI", value: "OpenAI"},
    {label: "Gemini", value: "Gemini"},
];

function createDefaultForm() {
    return {
        name: "",
        description: "",
        welcomeMessage: "Hello! How can I help you today?",
        tone: "Professional",
        llmProvider: "OpenAI",
        embeddingProvider: "OpenAI",
        isActive: true,
    };
}

export default {
    name: "AiAgentsView",
    components: {
        DashboardLayout,
        StateBlock,
        Dialog,
        VueSelect,
    },
    data() {
        return {
            loading: true,
            agents: [],
            isModalOpen: false,
            createSubmitting: false,
            llmProviderOptions,
            embeddingProviderOptions,
            form: createDefaultForm(),
        };
    },
    computed: {
        canCreateAgent() {
            return this.form.name.trim().length > 0;
        },
    },
    methods: {
        normalizeTone(tone) {
            return String(tone || "")
                .trim()
                .toLowerCase();
        },
        getToneBadgeClass(tone) {
            const normalizedTone = this.normalizeTone(tone);

            if (normalizedTone.includes("friendly") || normalizedTone.includes("warm") || normalizedTone.includes("empathetic")) {
                return "tone-friendly";
            }

            if (normalizedTone.includes("professional") || normalizedTone.includes("formal") || normalizedTone.includes("corporate")) {
                return "tone-professional";
            }

            if (normalizedTone.includes("casual") || normalizedTone.includes("relaxed")) {
                return "tone-casual";
            }

            if (normalizedTone.includes("technical") || normalizedTone.includes("expert") || normalizedTone.includes("analytical")) {
                return "tone-technical";
            }

            if (normalizedTone.includes("sales") || normalizedTone.includes("persuasive") || normalizedTone.includes("marketing")) {
                return "tone-sales";
            }

            return "tone-default";
        },
        getStatusBadgeClass(isActive) {
            return isActive ? "status-active" : "status-inactive";
        },
        resetForm() {
            this.form = createDefaultForm();
        },
        onModalHide() {
            if (!this.createSubmitting) {
                this.resetForm();
            }
        },
        async load() {
            this.loading = true;
            try {
                this.agents = (await api.get("/ai-agents")).data;
            } finally {
                this.loading = false;
            }
        },
        async create() {
            if (!this.canCreateAgent || this.createSubmitting) {
                return;
            }

            this.createSubmitting = true;
            try {
                await api.post("/ai-agents", this.form);
                this.isModalOpen = false;
                this.resetForm();
                await this.load();
            } finally {
                this.createSubmitting = false;
            }
        },
    },
    mounted() {
        this.load();
    },
};
</script>

<template>
    <DashboardLayout title="AI Agents" subtitle="Create, edit, and activate AI Agents for your company.">
        <div class="toolbar">
            <h2>Your AI Agents</h2>
            <div style="display: flex; gap: 12px">
                <button class="ghost-button" @click="load">Refresh</button>
                <button class="primary-button" @click="isModalOpen = true">+ Add AI Agent</button>
            </div>
        </div>
        <StateBlock v-if="!loading && agents.length === 0" title="No AI Agents yet" message="Use the form to create your first AI Agent." />
        <div v-else class="table-container">
            <table class="table">
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Tone</th>
                        <th>Status</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="agent in agents" :key="agent.id">
                        <td style="font-weight: 500">{{ agent.name }}</td>
                        <td>
                            <span class="badge tone-badge" :class="getToneBadgeClass(agent.tone)">{{ agent.tone }}</span>
                        </td>
                        <td>
                            <span class="badge status-badge" :class="getStatusBadgeClass(agent.isActive)">{{ agent.isActive ? "Active" : "Inactive" }}</span>
                        </td>
                        <td>
                            <RouterLink :to="`/ai-agents/${agent.id}`" class="edit-action-button">
                                <svg class="edit-action-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                    <path
                                        d="M4 20H8L18.5 9.5C19.3284 8.67157 19.3284 7.32843 18.5 6.5V6.5C17.6716 5.67157 16.3284 5.67157 15.5 6.5L5 17V20H4Z"
                                        stroke="currentColor"
                                        stroke-width="1.8"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                </svg>
                                <span>Edit Agent</span>
                            </RouterLink>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <Dialog
            v-model:visible="isModalOpen"
            modal
            :dismissableMask="true"
            :draggable="false"
            :style="{width: 'min(560px, calc(100vw - 2rem))'}"
            :breakpoints="{'960px': '90vw', '640px': '95vw'}"
            @hide="onModalHide"
        >
            <template #header>
                <div class="">
                    <h3 style="margin-top: unset">Create New AI Agent</h3>
                    <p class="modal-help">Create an agent profile. You can edit skills and advanced settings after creation.</p>
                </div>
            </template>
            <form id="create-agent-form" class="form-grid" @submit.prevent="create">
                <div class="field"><label>Name</label><input v-model="form.name" required placeholder="e.g. Alter AI" /></div>
                <div class="field"><label>Description</label><textarea v-model="form.description" rows="3" placeholder="What does this agent do?" /></div>
                <div class="field"><label>Welcome message</label><input v-model="form.welcomeMessage" /></div>
                <div class="field"><label>Tone</label><input v-model="form.tone" /></div>
                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px">
                    <div class="field-unstyle">
                        <label>Answer provider</label>
                        <VueSelect
                            v-model="form.llmProvider"
                            :options="llmProviderOptions"
                            label="label"
                            :reduce="(option) => option.value"
                            :clearable="false"
                            :appendToBody="true"
                            placeholder="Select answer provider"
                        />
                    </div>
                    <div class="field-unstyle">
                        <label>Embedding provider</label>
                        <VueSelect
                            v-model="form.embeddingProvider"
                            :options="embeddingProviderOptions"
                            label="label"
                            :reduce="(option) => option.value"
                            :clearable="false"
                            :appendToBody="true"
                            placeholder="Select embedding provider"
                        />
                    </div>
                </div>
                <label><input v-model="form.isActive" type="checkbox" /> Active status</label>
            </form>

            <template #footer>
                <button class="ghost-button" :disabled="createSubmitting" @click="isModalOpen = false">Cancel</button>
                <button type="submit" form="create-agent-form" class="primary-button" :disabled="!canCreateAgent || createSubmitting">
                    {{ createSubmitting ? "Creating..." : "Create AI Agent" }}
                </button>
            </template>
        </Dialog>
    </DashboardLayout>
</template>

<style scoped>
.badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.3rem 0.65rem;
    border-radius: 999px;
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.01em;
    border: 1px solid transparent;
    white-space: nowrap;
}

.tone-badge,
.status-badge {
    text-transform: capitalize;
}

.tone-default {
    color: #334155;
    background: #e2e8f0;
    border-color: #cbd5e1;
}

.tone-professional {
    color: #1d4ed8;
    background: #dbeafe;
    border-color: #bfdbfe;
}

.tone-friendly {
    color: #047857;
    background: #d1fae5;
    border-color: #a7f3d0;
}

.tone-casual {
    color: #7c3aed;
    background: #ede9fe;
    border-color: #ddd6fe;
}

.tone-technical {
    color: #0f766e;
    background: #ccfbf1;
    border-color: #99f6e4;
}

.tone-sales {
    color: #b45309;
    background: #fef3c7;
    border-color: #fde68a;
}

.status-active {
    color: #166534;
    background: #dcfce7;
    border-color: #bbf7d0;
}

.status-inactive {
    color: #991b1b;
    background: #fee2e2;
    border-color: #fecaca;
}

.edit-action-button {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    padding: 0.45rem 0.72rem;
    border-radius: 0.55rem;
    border: 1px solid color-mix(in srgb, var(--button-primary) 30%, transparent);
    color: var(--button-primary);
    background: color-mix(in srgb, var(--button-primary) 10%, white);
    font-weight: 600;
    font-size: 0.86rem;
    text-decoration: none;
    transition: all 0.16s ease;
}

.edit-action-button:hover {
    background: color-mix(in srgb, var(--button-primary) 16%, white);
    border-color: color-mix(in srgb, var(--button-primary) 45%, transparent);
    transform: translateY(-1px);
}

.edit-action-button:focus-visible {
    outline: 2px solid color-mix(in srgb, var(--button-primary) 50%, transparent);
    outline-offset: 2px;
}

.edit-action-icon {
    width: 14px;
    height: 14px;
    flex: 0 0 14px;
}
</style>
